import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document } from '@contentful/rich-text-types';
import { useMediaQuery, useTheme, Box, Typography } from '@mui/material';
import { Children, useState, ReactNode } from 'react';
import { CardRichTextDescriptionLinks, SectionContentRichTextContentLinks, ContentTypeRichTextSecondaryContentLinks, ContentTypeRichTextContentLinks, TextBlockSeoContentLinks, TermsAndConditionsContentLinks, InsuranceIntroLinks, TableFootnoteLinks, ContentSectionDescriptionLinks } from '@/types/generated';
import { getOptions } from './RichText.helpers';
import TAGLink from '@shared-components/components/TAGLink';
import { incorrectUsageWarning } from '@utils/miscUtils';
export type RichTextContent = Document;
interface IRichText {
  /**
   * Rich Text content background check coming from Contentful.
   *
   * @type {RichTextContent}
   * @memberof IRichText
   */
  isDark?: boolean;
  /**
   * Optional props to pass to the RichText component. **Note that for multi-element rich text these props will be passed to all elements**.
   *
   * @type {Record<string, unknown>}
   * @memberof IRichText
   * Future enhancement: allow passing props per rich text element type
   * for example { paragraph: {...}, header1: {...}, ... } This will make styling complex rich text easier, but will require to know in advance what types of rich text elements are going to be present in the content.
   */
  docProps?: Record<string, unknown>;
  /**
   * Rich Text content coming from Contentful.
   *
   * @type {RichTextContent}
   * @memberof IRichText
   */
  content: RichTextContent;
  /**
   * Secondary Rich Text content coming from Contentful.
   *
   * @type {RichTextContent}
   * @memberof IRichText
   */
  secondaryContent?: RichTextContent;
  /**
   * Links object containing additional data about entries and assets linked in the rich text.
   *
   * @type {SectionContentRichTextContentLinks}
   * @memberof IRichText
   */
  links?: CardRichTextDescriptionLinks | SectionContentRichTextContentLinks | ContentTypeRichTextContentLinks | TextBlockSeoContentLinks | TermsAndConditionsContentLinks | InsuranceIntroLinks | TableFootnoteLinks | ContentSectionDescriptionLinks;
  /**
   * Secondary Links object containing additional data about entries and assets linked in the secondary rich text.
   *
   * @type {ContentTypeRichTextSecondaryContentLinks}
   * @memberof IRichText
   */
  secondaryLinks?: ContentTypeRichTextSecondaryContentLinks;
  /**
   * Display SecondaryContent in DOM for SEO purposes
   *
   * @type {boolean}
   * @memberof IContentTypeRichText
   */
  displaySecondaryContentInDom?: boolean;
}
export default function RichText({
  isDark = false,
  content,
  links,
  secondaryContent,
  secondaryLinks,
  docProps = {},
  displaySecondaryContentInDom = false
}: Readonly<IRichText>) {
  const [state, setState] = useState({
    expanded: false
  });
  const {
    expanded
  } = state;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  if (!content) {
    incorrectUsageWarning('RichText', 'The content prop is missing.');
    return null;
  }

  // documentToReactComponents only returns an array when the root node is recognized as a "document";
  // otherwise (e.g. malformed CMS rich text data) it returns a single ReactNode.
  const toReactNodeArray = (node: ReactNode): ReactNode[] => Children.toArray(node);
  const primaryRichContent = toReactNodeArray(documentToReactComponents(content, getOptions({
    docProps,
    links,
    isDark,
    theme,
    isSmallScreen
  })));
  const secondaryRichContent = secondaryContent && toReactNodeArray(documentToReactComponents(secondaryContent, getOptions({
    docProps,
    links: secondaryLinks,
    isDark,
    theme,
    isSmallScreen
  })));
  const handleMoreOrLess = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault();
    setState({
      ...state,
      expanded: !state.expanded
    });
  };

  // this will add more/less link to show/hide text
  const addMoreOrLessLink = () => {
    const linkText = expanded ? '...Less' : '...More';
    const moreOrLessLink = <Typography variant="bodyMediumBook" color={isDark ? 'text.light' : 'text.interactive'} key={`${linkText}-link`}>
				<TAGLink onClick={handleMoreOrLess} dataTestId={`link_ellipsis_${linkText}`}>
					{linkText}
				</TAGLink>
			</Typography>;
    const elementsToRender = secondaryRichContent && expanded ? [...primaryRichContent, ...secondaryRichContent] : primaryRichContent;

    // Return as JSX fragment with all elements
    return <>
				{elementsToRender.map(element => element)}
				{moreOrLessLink}
			</>;
  };

  // secondary content available in dom only when clicking more
  const richTextContent = secondaryRichContent ? addMoreOrLessLink() : <>{primaryRichContent.map(element => element)}</>;

  // secondary content available in dom but hidden using css for crawlers to read the content
  // example rich text block seo content
  const richTextContentWithSecondaryContentInDom = <>
			{primaryRichContent}
			{!expanded && <Typography variant="bodyMediumBook" color={isDark ? 'text.light' : 'text.interactive'}>
					<TAGLink onClick={handleMoreOrLess} dataTestId={`link_ellipsis_...More-link}`}>
						...More
					</TAGLink>
				</Typography>}
			<Box sx={{
      display: expanded ? 'block' : 'none'
    }}>
				{secondaryRichContent}
				<Typography variant="bodyMediumBook" color={isDark ? 'text.light' : 'text.interactive'}>
					<TAGLink onClick={handleMoreOrLess} dataTestId={`link_ellipsis_...Less-link}`}>
						...Less
					</TAGLink>
				</Typography>
			</Box>
		</>;
  return displaySecondaryContentInDom ? richTextContentWithSecondaryContentInDom : richTextContent;
}